import { useEffect, useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { MoreHorizontal, Upload } from "lucide-react";
import { useIoService } from "./useIo";
import { ImportUploadDialog } from "./ImportUploadDialog";
import type { TemplateBrief } from "../services/types";

// Toolbar "more options" segment. Rendered as a `btn-split__wand` so it joins
// the "+ New" split-button group (same primary colour, attached, with a
// divider). Its menu offers "Import Records". Auto-shown by ActionControlPanel
// when ≥1 import template targets the current list's model; renders nothing
// otherwise, so the split group stays just "+ New".
export function ImportButton({ modelKey }: { modelKey?: string }) {
    const io = useIoService();
    const [templates, setTemplates] = useState<TemplateBrief[] | null>(null);
    const [chosen, setChosen] = useState<string | null>(null);

    useEffect(() => {
        if (!modelKey) {
            setTemplates([]);
            return;
        }
        let cancelled = false;
        io.templatesForModel(modelKey)
            .then((t) => !cancelled && setTemplates(t))
            .catch(() => !cancelled && setTemplates([]));
        return () => {
            cancelled = true;
        };
    }, [io, modelKey]);

    if (!templates || templates.length === 0) return null;

    const chosenTemplate = templates.find((t) => t.code === chosen) ?? null;
    const single = templates.length === 1;
    const itemClass =
        "flex items-center gap-2 px-3 py-2 text-sm text-foreground outline-none cursor-pointer data-[highlighted]:bg-muted";

    return (
        <>
            <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                    <button type="button" className="btn-split__wand" aria-label="More options" title="More options">
                        <MoreHorizontal size={16} />
                    </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                    <DropdownMenu.Content
                        align="start"
                        sideOffset={6}
                        className="z-50 min-w-56 rounded-md border border-border bg-card py-1 shadow-lg"
                    >
                        {single ? (
                            <DropdownMenu.Item className={itemClass} onSelect={() => setChosen(templates[0].code)}>
                                <Upload size={14} className="text-muted-foreground" />
                                Import Records
                            </DropdownMenu.Item>
                        ) : (
                            <>
                                <DropdownMenu.Label className="px-3 pb-1 pt-1.5 text-xs font-medium text-muted-foreground">
                                    Import Records
                                </DropdownMenu.Label>
                                {templates.map((t) => (
                                    <DropdownMenu.Item key={t.code} className={itemClass} onSelect={() => setChosen(t.code)}>
                                        <Upload size={14} className="text-muted-foreground" />
                                        {t.label}
                                    </DropdownMenu.Item>
                                ))}
                            </>
                        )}
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>

            {chosenTemplate && (
                <ImportUploadDialog
                    templateCode={chosenTemplate.code}
                    templateLabel={chosenTemplate.label}
                    approvalRequired={chosenTemplate.approval_required}
                    onClose={() => setChosen(null)}
                />
            )}
        </>
    );
}
